/**
 * What is waiting on me, and one application in detail.
 *
 * The detail view is where the workflow becomes visible: every revision, what each one
 * changed, who decided what and why. The diff between consecutive revisions is the question
 * the client's manual asks by name — *what did the role above me change?* — and it is
 * computed on the server so it is the same one every role sees.
 *
 * The buttons come from `available_actions`, which the API works out for this caller. Nothing
 * here decides who may approve; offering an action and then refusing it on click is how a
 * governance tool loses trust in small increments.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api, ApiError } from '@/api/client'
import type { ApplicationSummary, Envelope } from '@/api/types'
import { navigate } from '@/app/routes'
import { Icon } from '@/components/Icon'
import { Empty, Failed, Skeleton } from '@/components/states'
import { formatDate } from '@/lib/format'
import { decisionLabel, fieldLabel, roleLabel } from '@/lib/labels'

type Change = { field: string; kind: string; before: unknown; after: unknown }
type Revision = {
  revision_no: number
  author_role: string | null
  comment: string | null
  via_ai: boolean
  created_at: string
  diff: { count: number; fields: string[]; changes: Change[] }
}
type Step = {
  step_no: number
  role_code: string
  decision: string
  decided_at: string | null
  reason: string | null
}
type ApplicationDetail = ApplicationSummary & {
  route_steps: string[]
  payload: Record<string, unknown>
  available_actions: string[]
  revisions: Revision[]
  steps: Step[]
}

export function Inbox() {
  const { t, i18n } = useTranslation()

  const items = useQuery({
    queryKey: ['inbox'],
    queryFn: ({ signal }) =>
      api.get<Envelope<ApplicationSummary>>('/inbox?limit=50&sort=-updated_at', signal),
  })

  if (items.isError) {
    const error = items.error
    return (
      <main className="page">
        <Failed
          message={error instanceof ApiError ? error.friendly : String(error)}
          onRetry={() => void items.refetch()}
        />
      </main>
    )
  }

  return (
    <main className="page" data-testid="inbox">
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('nav.inbox')}</h1>
          <p className="page__subtitle">{t('inbox.subtitle')}</p>
        </div>
      </header>

      {items.isPending ? (
        <Skeleton rows={5} />
      ) : items.data.items.length === 0 ? (
        <Empty title={t('inbox.empty')} hint={t('inbox.emptyHint')} />
      ) : (
        <div className="grid__scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('inbox.number')}</th>
                <th scope="col">{t('inbox.what')}</th>
                <th scope="col">{t('inbox.status')}</th>
                <th scope="col">{t('inbox.with')}</th>
                <th scope="col">{t('inbox.updated')}</th>
              </tr>
            </thead>
            <tbody>
              {items.data.items.map((application) => (
                <tr key={application.id} data-testid="inbox-row">
                  <td className="numeric">
                    <button
                      type="button"
                      className="link"
                      onClick={() =>
                        navigate({ name: 'application', applicationId: application.id })
                      }
                      data-testid="inbox-open"
                    >
                      {application.number}
                    </button>
                  </td>
                  <td>{application.title}</td>
                  <td>
                    <span className={`chip chip--${application.status}`}>
                      {t(`applicationStatus.${application.status}`, application.status)}
                    </span>
                  </td>
                  <td>{roleLabel(t, application.current_role)}</td>
                  <td>{formatDate(application.updated_at, i18n.language)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  )
}

export function Application({ applicationId }: { applicationId: string }) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const application = useQuery({
    queryKey: ['application', applicationId],
    queryFn: ({ signal }) =>
      api.get<ApplicationDetail>(`/applications/${applicationId}`, signal),
  })

  const decide = useMutation({
    mutationFn: (action: string) =>
      api.post(`/applications/${applicationId}/${action}`, { reason: reason || null }),
    onSuccess: () => {
      setReason('')
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['application', applicationId] })
      void queryClient.invalidateQueries({ queryKey: ['inbox'] })
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.friendly : String(caught)),
  })

  if (application.isPending) {
    return (
      <main className="page" data-testid="application">
        <Skeleton rows={6} />
      </main>
    )
  }

  if (application.isError) {
    const failure = application.error
    return (
      <main className="page">
        <Failed
          message={failure instanceof ApiError ? failure.friendly : String(failure)}
          onRetry={() => void application.refetch()}
        />
      </main>
    )
  }

  const detail = application.data
  // Reject and return require a reason; the server enforces it, and disabling the button
  // until there is one says so before the click rather than after.
  const needsReason = (action: string) => action === 'reject' || action === 'return'

  return (
    <main className="page" data-testid="application">
      <header className="page__header">
        <div>
          <p className="page__eyebrow">
            <button type="button" className="link" onClick={() => navigate({ name: 'inbox' })}>
              {t('nav.inbox')}
            </button>
          </p>
          <h1 className="page__title">{detail.title}</h1>
          <p className="page__subtitle numeric">{detail.number}</p>
        </div>
        <span className={`chip chip--${detail.status}`}>
          {t(`applicationStatus.${detail.status}`, detail.status)}
        </span>
      </header>

      <section className="panel" data-testid="route">
        <h2 className="panel__title">{t('application.route')}</h2>
        <ol className="route">
          {detail.route_steps.map((role, index) => (
            <li
              key={role}
              className="route__step"
              data-state={
                detail.status === 'approved' || index < detail.current_step
                  ? 'done'
                  : index === detail.current_step && !['approved', 'rejected', 'cancelled'].includes(detail.status)
                    ? 'current'
                    : 'waiting'
              }
            >
              <span className="route__role">{roleLabel(t, role)}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="panel" data-testid="revisions">
        <h2 className="panel__title">{t('application.history')}</h2>
        {detail.revisions.map((revision) => (
          <article key={revision.revision_no} className="revision">
            <p className="revision__head">
              <span className="revision__no numeric">#{revision.revision_no}</span>
              <span className="revision__author">{roleLabel(t, revision.author_role)}</span>
              <span className="revision__when">
                {formatDate(revision.created_at, i18n.language)}
              </span>
              {revision.via_ai ? <span className="chip chip--ai">{t('application.viaAi')}</span> : null}
            </p>
            {revision.comment ? <p className="revision__comment">{revision.comment}</p> : null}
            {revision.diff.changes.length > 0 ? (
              <table className="diff">
                <tbody>
                  {revision.diff.changes.map((change) => (
                    <tr key={change.field} data-field={change.field}>
                      <th scope="row">{fieldLabel(change.field)}</th>
                      <td className="diff__before">
                        <span>{render(change.before, t)}</span>
                      </td>
                      {/* Decorative: the row already reads "field, old value, new value" to
                          a screen reader, and an arrow glyph in between would be announced. */}
                      <td className="diff__arrow" aria-hidden="true">
                        <Icon name="chevronEnd" size={13} strokeWidth={2.4} />
                      </td>
                      <td className="diff__after">
                        <span>{render(change.after, t)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="revision__none">{t('application.noChanges')}</p>
            )}
          </article>
        ))}
      </section>

      {detail.steps.length > 0 ? (
        <section className="panel" data-testid="decisions">
          <h2 className="panel__title">{t('application.decisions')}</h2>
          <ul className="decisions">
            {detail.steps.map((step, index) => (
              <li key={`${step.step_no}-${index}`}>
                <span className={`chip chip--${step.decision}`}>
                  {decisionLabel(t, step.decision)}
                </span>{' '}
                <strong>{roleLabel(t, step.role_code)}</strong>{' '}
                {step.decided_at ? formatDate(step.decided_at, i18n.language) : ''}
                {step.reason ? <em> — {step.reason}</em> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {detail.available_actions.length > 0 ? (
        <section className="panel" data-testid="actions">
          <h2 className="panel__title">{t('application.yourTurn')}</h2>
          <label className="field">
            <span className="field__label">{t('application.reason')}</span>
            <textarea
              className="field__input field__input--area"
              rows={2}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              data-testid="decision-reason"
            />
          </label>
          {error ? (
            <p className="signin__error" role="alert" data-testid="decision-error">
              {error}
            </p>
          ) : null}
          <div className="edit__actions">
            {detail.available_actions
              .filter((action) => action !== 'amend')
              .map((action) => (
                <button
                  key={action}
                  type="button"
                  className={`button ${action === 'approve' ? 'button--primary' : 'button--quiet'}`}
                  disabled={decide.isPending || (needsReason(action) && !reason.trim())}
                  onClick={() => decide.mutate(action)}
                  data-testid={`decide-${action}`}
                >
                  {t(`application.${action}`, action)}
                </button>
              ))}
          </div>
        </section>
      ) : null}
    </main>
  )
}

/**
 * The server's diff arrives without column kinds — it describes *what changed*, not what
 * type the field was — so this renders values as they came rather than reaching for
 * `formatValue`. Booleans are still words, and words are translated.
 */
function render(value: unknown, t: (key: string) => string): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return t(value ? 'value.yes' : 'value.no')
  return String(value)
}
